/**
 * KaraokeCardLiveStreamTalent — Karaoke talent live page.
 *
 * Redesigned with the Sing4You design system (sing4you-live.css).
 * Replaces the old delegation to the 1326-line NewKaraokeCard.tsx.
 *
 * Layout:
 *   Header  — 🎤 KARAOKE branding · HOST VIEW badge · ● LIVE + viewer count
 *   Body    :
 *     Left  — video area (LocalUser) + KaraokeLyrics panel (wired to AudioMixUpdated)
 *     Right — sidebar (CHAT | VIEWERS | QUEUE, reactions, perf tools, queue)
 *
 * Karaoke audio flow:
 *   AudioMixUpdated (modal) ──onLyricsChange──► lyricsLrc state
 *                           ──onLyricsTimeChange►lyricsTime state
 *                           ──onPlayStateChange──►isKaraokePlaying state
 *   KaraokeLyrics (panel)   ◄── all of the above + derived duration/title
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  LocalUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
} from "agora-rtc-react";
import AgoraRTM from "agora-rtm-sdk";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Typography,
} from "@mui/material";
import {
  Build,
  CallEnd,
  Close,
  Fullscreen,
  FullscreenExit,
  Mic,
  MicOff,
  Send,
  Videocam,
  VideocamOff,
  Visibility,
} from "@mui/icons-material";

import AgoraWrapper from "../AgoraWrapper";
import AudioMixUpdated, { type AudioMixUpdatedRef } from "./AudioMixUpdated";
import KaraokeLyrics from "./KaraokeLyrics";
import LiveChat from "../../LiveChat";
import LivePolls from "../../LivePolls";
import BingoManagement from "../../Talent/BingoManagement";
import GuestManagement from "../../Talent/GuestManagement_updated";
import YoutubeRequest from "../../Talent/YoutubeRequest";
import ListofSongsManagement, { SongItem } from "../../Talent/ListofSongsManagement";

import { AGORA_APP_ID, AGORA_RTM_APP_ID } from "../../../../constants/config";
import { getAgoraToken } from "../../../../services/api/agora.api";
import { useEndFlashCard } from "./apis/NewKarokeApi";
import { useAuth, useCurrentTalentProfile } from "../../../../hooks";
import useCurrentBusinessInfo from "../../../../hooks/useCurrentBusiness";

import "../../../../styles/sing4you-live.css";

/* ── Helpers ─────────────────────────────────────────────────── */
/** Sidebar tabs — always-visible panel per role */
type SidebarTab = "chat" | "guests" | "youtube" | "polls";

/** Extra tools accessible via the drawer (not already in the sidebar) */
type ToolId = "bingo" | "songlist";

const TOOL_META: Record<ToolId, { label: string; emoji: string; desc: string }> = {
  bingo:    { label: "Bingo Break", emoji: "🎯", desc: "Play bingo with your viewers" },
  songlist: { label: "Song List",   emoji: "🎵", desc: "Manage your song queue" },
};

/** Extracts the last timestamp from an LRC string as an approximate duration. */
function parseLrcDuration(lrc: string): number {
  let max = 0;
  for (const line of lrc.split(/\r?\n/)) {
    const m = line.match(/\[(\d{1,2}):(\d{1,2})(?:\.(\d{1,2}))?\]/);
    if (m) {
      const t = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + parseInt(m[3] ?? "0", 10) / 100;
      if (t > max) max = t;
    }
  }
  return max;
}

/** Extracts [ti:…] and [ar:…] tags from an LRC string. */
function parseLrcTitle(lrc: string): string {
  const title  = lrc.match(/\[ti:(.+?)\]/i)?.[1]?.trim() ?? "";
  const artist = lrc.match(/\[ar:(.+?)\]/i)?.[1]?.trim() ?? "";
  if (title && artist) return `${title} — ${artist}`;
  return title || artist;
}

/* ── Inner component (uses Agora hooks — must be inside AgoraRTCProvider) ── */
function KaraokeLiveInner() {
  const { channel_name }    = useParams<{ channel_name: string }>();
  const navigate            = useNavigate();
  const [searchParams]      = useSearchParams();
  const flashCardId         = searchParams.get("flashCardId");

  const { userInfo }        = useAuth();
  const { currentProfile }  = useCurrentTalentProfile();
  const { currentBusiness } = useCurrentBusinessInfo();

  const userId  = currentProfile?.id != null
    ? currentProfile.id.toString()
    : (userInfo?.id?.toString() ?? "");
  const videoId = channel_name ?? (userId ? `karaoke-${userId}` : "karaoke-default");

  /* ── Session management ─────────────────────────────────────── */
  const { update: endFlashCard } = useEndFlashCard({
    key:    ["end-flash-card", flashCardId],
    params: {
      id:      Number(flashCardId),
      headers: { "talent-id": currentProfile?.id?.toString() ?? "" },
    },
  });

  /* ── UI state ────────────────────────────────────────────────── */
  const [micOn,          setMicOn]          = useState(true);
  const [cameraOn,       setCameraOn]       = useState(true);
  const [isFullscreen,   setIsFullscreen]   = useState(false);
  const [showEndDialog,  setShowEndDialog]  = useState(false);
  const [audioMixOpen,   setAudioMixOpen]   = useState(false);
  const [activeTab,      setActiveTab]      = useState<SidebarTab>("chat");
  const [token,          setToken]          = useState<string | null>(null);
  const [viewerCount,    setViewerCount]    = useState(0);
  const [chatInput,      setChatInput]      = useState("");
  const [volume,         setVolume]         = useState(50);

  /* ── Creator Tools state ──────────────────────────────────────── */
  const [toolsOpen,     setToolsOpen]     = useState(false);
  const [activeTools,   setActiveTools]   = useState<ToolId[]>([]);
  const [songList,      setSongList]      = useState<SongItem[]>([]);
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);

  /* ── Karaoke state (fed by AudioMixUpdated callbacks) ────────── */
  const [lyricsLrc,        setLyricsLrc]        = useState<string>("");
  const [lyricsTime,       setLyricsTime]       = useState(0);
  const [isKaraokePlaying, setIsKaraokePlaying] = useState(false);
  const [karaokeDuration,  setKaraokeDuration]  = useState(0);
  const [songTitle,        setSongTitle]        = useState("");

  const fullscreenRef = useRef<HTMLDivElement>(null);
  const rtmClientRef  = useRef<ReturnType<typeof AgoraRTM.createInstance> | null>(null);
  const rtmChannelRef = useRef<ReturnType<ReturnType<typeof AgoraRTM.createInstance>["createChannel"]> | null>(null);
  const audioMixRef   = useRef<AudioMixUpdatedRef>(null);

  /* ── Incoming reactions from host / audience ─────────────────── */
  const [flyingEmojis, setFlyingEmojis] = useState<
    { id: number; emoji: string; x: number }[]
  >([]);
  const emojiIdRef = useRef(0);

  /* ── Agora RTC token ─────────────────────────────────────────── */
  useEffect(() => {
    if (!userId || !videoId) return;
    getAgoraToken(videoId, userId)
      .then(setToken)
      .catch((err) => console.error("[KaraokeLive] Token error:", err));
  }, [videoId, userId]);

  /* ── Agora RTC hooks ─────────────────────────────────────────── */
  useJoin(
    { appid: AGORA_APP_ID!, channel: videoId, token: token!, uid: userId },
    !!token && !!userId,
  );
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(true);
  const { localCameraTrack }     = useLocalCameraTrack(true);
  usePublish([localMicrophoneTrack, localCameraTrack]);

  const remoteUsers             = useRemoteUsers();
  const { audioTracks }         = useRemoteAudioTracks(remoteUsers);
  useEffect(() => { audioTracks.forEach((t) => t.play()); }, [audioTracks]);

  useEffect(() => {
    if (!localCameraTrack) return;
    localCameraTrack
      .setEncoderConfiguration({ width: 1280, height: 720, frameRate: 30, bitrateMax: 3000 })
      .catch(() => {});
  }, [localCameraTrack]);

  /* ── Agora RTM for viewer count ──────────────────────────────── */
  useEffect(() => {
    if (!AGORA_RTM_APP_ID || !userId || !videoId) return;
    let mounted = true;

    const setup = async () => {
      try {
        const client  = AgoraRTM.createInstance(AGORA_RTM_APP_ID);
        await client.login({ uid: userId });
        if (!mounted) { await client.logout().catch(() => {}); return; }

        const channel = client.createChannel(videoId);
        await channel.join();

        const updateCount = async () => {
          try { const m = await channel.getMembers(); if (mounted) setViewerCount(m.length); } catch {}
        };
        channel.on("MemberJoined", updateCount);
        channel.on("MemberLeft",   updateCount);
        updateCount();

        // Receive reaction emojis sent by host / audience
        channel.on("ChannelMessage", (message: any) => {
          try {
            const msg = JSON.parse(message.text ?? "{}");
            if (msg.type === "reaction" && msg.emoji && mounted) {
              const id = ++emojiIdRef.current;
              const x  = 10 + Math.random() * 70;
              setFlyingEmojis((prev) => [...prev, { id, emoji: msg.emoji, x }]);
              setTimeout(() => {
                setFlyingEmojis((prev) => prev.filter((e) => e.id !== id));
              }, 2500);
            }
          } catch {}
        });

        rtmClientRef.current  = client;
        rtmChannelRef.current = channel;
      } catch (err) {
        console.warn("[KaraokeLive RTM]", err);
      }
    };
    setup();

    return () => {
      mounted = false;
      rtmChannelRef.current?.leave().catch(() => {});
      rtmClientRef.current?.logout().catch(() => {});
    };
  }, [userId, videoId]);

  /* ── Fullscreen ──────────────────────────────────────────────── */
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const toggleFullscreen = () => {
    if (isFullscreen) document.exitFullscreen().catch(() => {});
    else fullscreenRef.current?.requestFullscreen().catch(() => {});
  };

  /* ── Track controls ──────────────────────────────────────────── */
  const toggleMic = () => {
    localMicrophoneTrack?.setEnabled(!micOn).catch(() => {});
    setMicOn((p) => !p);
  };
  const toggleCamera = () => {
    localCameraTrack?.setEnabled(!cameraOn).catch(() => {});
    setCameraOn((p) => !p);
  };

  /* ── Session end ─────────────────────────────────────────────── */
  const cleanupTracks = useCallback(() => {
    localMicrophoneTrack?.close();
    localCameraTrack?.close();
  }, [localMicrophoneTrack, localCameraTrack]);

  const handleEndStream = useCallback(async () => {
    cleanupTracks();
    let streamId: string | undefined;
    if (flashCardId) {
      const stored = localStorage.getItem(`youtube_stream_id_flash_${flashCardId}`);
      if (stored) streamId = stored;
    }
    try {
      await endFlashCard(streamId ? { stream_id: streamId } : {}, {
        onSuccess: () => {
          if (flashCardId && streamId) {
            localStorage.removeItem(`youtube_stream_id_flash_${flashCardId}`);
          }
          enqueueSnackbar("Karaoke session ended successfully", { variant: "success" });
          window.location.href = "/taascards?published-tab=Flash_Card";
        },
      });
    } catch {
      enqueueSnackbar("Failed to end session", { variant: "error" });
    }
    setShowEndDialog(false);
  }, [endFlashCard, flashCardId, cleanupTracks]);

  const handleLeave = useCallback(() => {
    cleanupTracks();
    navigate("/");
  }, [navigate, cleanupTracks]);

  /* ── Karaoke callbacks (from AudioMixUpdated) ────────────────── */
  const handleLyricsChange = useCallback((lrc: string | null) => {
    const text = lrc ?? "";
    setLyricsLrc(text);
    // Duration from LRC is a fallback; onDurationChange from AudioMixUpdated is preferred.
    if (text) setKaraokeDuration((prev) => prev > 0 ? prev : parseLrcDuration(text));
    setSongTitle(text ? parseLrcTitle(text) : "");
    setLyricsTime(0);
  }, []);

  const handleLyricsTimeChange = useCallback((t: number) => {
    setLyricsTime(t);
  }, []);

  const handlePlayStateChange = useCallback((playing: boolean) => {
    setIsKaraokePlaying(playing);
  }, []);

  /* ── Lyrics-panel control callbacks → imperative ref ─────────── */
  const handlePlayPause = useCallback(() => {
    if (isKaraokePlaying) audioMixRef.current?.pause();
    else audioMixRef.current?.play();
  }, [isKaraokePlaying]);

  const handlePrev = useCallback(() => {
    audioMixRef.current?.stop();
  }, []);

  const handleNext = useCallback(() => {
    // Queue functionality placeholder — restart for now.
    audioMixRef.current?.stop();
  }, []);

  const handleSeek = useCallback((seconds: number) => {
    audioMixRef.current?.seekTo(seconds);
  }, []);

  const handleVolumeChange = useCallback((v: number) => {
    setVolume(v);
    audioMixRef.current?.setVol(v);
  }, []);

  /* ── Tool management ────────────────────────────────────────────── */
  const toggleTool = useCallback((id: ToolId) => {
    setActiveTools((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
    setToolsOpen(false);
  }, []);

  const removeTool = useCallback((id: ToolId) => {
    setActiveTools((prev) => prev.filter((t) => t !== id));
  }, []);

  const handleSongAdded = useCallback((song: SongItem) => {
    const url = song?.url != null ? String(song.url).trim() : "";
    if (!url) return;
    setSongList((prev) => [...prev, { ...song, url }]);
    // Auto-open the Song List panel when first song is added
    setActiveTools((prev) => (prev.includes("songlist") ? prev : [...prev, "songlist"]));
  }, []);

  const handleSongSelect = useCallback((song: SongItem) => {
    const url = song.url ?? "";
    if (!url) { enqueueSnackbar("This song has no audio URL yet.", { variant: "warning" }); return; }
    setActiveTrackId(song.id);
    audioMixRef.current?.loadUrl(url, song.name);
    enqueueSnackbar(`Loading "${song.name}" into Karaoke Studio…`, { variant: "info" });
  }, []);

  const displayName = currentProfile?.username ?? userInfo?.username ?? userId;

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <div className="s4y-page" ref={fullscreenRef}>

      {/* ── Header ──────────────────────────────────────────── */}
      <header className="s4y-header">
        <div className="s4y-logo">
          <span className="s4y-logo-icon">🎤</span>
          <div className="s4y-logo-text">
            <span className="s4y-logo-title">KARAOKE</span>
            <span className="s4y-logo-sub">Live Performance</span>
          </div>
        </div>

        <div className="s4y-role-badge">
          🎤&nbsp;&nbsp;HOST
        </div>

        <div className="s4y-header-right">
          <button
            className="s4y-tools-btn"
            onClick={() => setToolsOpen(true)}
            title="Creator Tools"
          >
            <Build sx={{ fontSize: "0.9rem" }} />
            Tools
            {activeTools.length > 0 && (
              <span className="s4y-tools-badge">{activeTools.length}</span>
            )}
          </button>
          <span className="s4y-live-pill">
            <span className="s4y-live-dot" />
            LIVE
          </span>
          <span className="s4y-viewer-count">
            <Visibility sx={{ fontSize: "0.9rem", opacity: 0.65 }} />
            {viewerCount}
          </span>
        </div>
      </header>

      {/* ── Body ────────────────────────────────────────────── */}
      <div className="s4y-body">

        {/* ── Left: video + lyrics ────────────────────────── */}
        <main className="s4y-main">

          {/* Video area */}
          <div className="s4y-video-area" style={{ position: "relative" }}>
            <div className="s4y-video-inner">
              <LocalUser
                cameraOn={cameraOn}
                micOn={micOn}
                videoTrack={localCameraTrack}
                audioTrack={localMicrophoneTrack}
                style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#0c1128" }}
              />
            </div>

            {/* Flying emoji reactions from host / audience */}
            {flyingEmojis.map(({ id, emoji, x }) => (
              <span
                key={id}
                className="s4y-flying-emoji"
                style={{ left: `${x}%`, bottom: "22%" }}
              >
                {emoji}
              </span>
            ))}

            {!cameraOn && (
              <div className="s4y-cam-placeholder">
                <span className="s4y-cam-placeholder-icon">🎤</span>
                <span className="s4y-cam-placeholder-text">Your Camera</span>
              </div>
            )}

            <div className="s4y-username-badge">
              <span className="s4y-badge-dot" />
              {displayName}
            </div>

            <div className="s4y-video-badge">{viewerCount}</div>

            <div className="s4y-controls-bar">
              <button
                className={`s4y-ctrl-btn ${micOn ? "on" : "off"}`}
                onClick={toggleMic}
                title={micOn ? "Mute" : "Unmute"}
              >
                {micOn
                  ? <Mic      sx={{ fontSize: "1.1rem" }} />
                  : <MicOff  sx={{ fontSize: "1.1rem" }} />}
              </button>

              <button
                className={`s4y-ctrl-btn ${cameraOn ? "on" : "off"}`}
                onClick={toggleCamera}
                title={cameraOn ? "Camera off" : "Camera on"}
              >
                {cameraOn
                  ? <Videocam    sx={{ fontSize: "1.1rem" }} />
                  : <VideocamOff sx={{ fontSize: "1.1rem" }} />}
              </button>

              <span className="s4y-ctrl-divider" />

              <button
                className="s4y-ctrl-btn"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen
                  ? <FullscreenExit sx={{ fontSize: "1.1rem" }} />
                  : <Fullscreen    sx={{ fontSize: "1.1rem" }} />}
              </button>

              <span className="s4y-ctrl-divider" />

              <button
                className="s4y-ctrl-btn end"
                onClick={() => setShowEndDialog(true)}
                title="End session"
              >
                <CallEnd sx={{ fontSize: "1.1rem" }} />
              </button>
            </div>
          </div>

          {/* ── Karaoke lyrics panel ──────────────────────── */}
          <KaraokeLyrics
            lrc={lyricsLrc}
            currentTime={lyricsTime}
            duration={karaokeDuration}
            songTitle={songTitle || undefined}
            isPlaying={isKaraokePlaying}
            volume={volume}
            onPlayPause={handlePlayPause}
            onPrev={handlePrev}
            onNext={handleNext}
            onSeek={handleSeek}
            onVolumeChange={handleVolumeChange}
            onSettingsClick={() => setAudioMixOpen(true)}
          />

          {/* ── Active creator tool panels (bingo + songlist from drawer) ── */}
          {activeTools.map((id) => (
            <div key={id} className="s4y-tool-panel">
              <div className="s4y-tool-panel-header">
                <span>{TOOL_META[id].emoji}&nbsp; {TOOL_META[id].label}</span>
                <button className="s4y-tool-panel-close" onClick={() => removeTool(id)} title="Close">
                  <Close sx={{ fontSize: "0.9rem" }} />
                </button>
              </div>
              <div className="s4y-tool-panel-body">
                {id === "bingo"    && <BingoManagement bookingId={flashCardId ?? ""} />}
                {id === "songlist" && (
                  <ListofSongsManagement
                    songs={songList}
                    activeSongId={activeTrackId}
                    onSongAdded={handleSongAdded}
                    onSongSelect={handleSongSelect}
                    emptyMessage="Add songs, then click one to load it into the Karaoke Studio."
                  />
                )}
              </div>
            </div>
          ))}
        </main>

        {/* ── Right: sidebar ──────────────────────────────── */}
        <aside className="s4y-sidebar">

          <div className="s4y-sidebar-tabs">
            {([
              { id: "chat",    label: "CHAT"   },
              { id: "guests",  label: "GUESTS" },
              { id: "youtube", label: "QUEUE"  },
              { id: "polls",   label: "POLLS"  },
            ] as { id: SidebarTab; label: string }[]).map(({ id, label }) => (
              <button
                key={id}
                className={`s4y-sidebar-tab ${activeTab === id ? "active" : ""}`}
                onClick={() => setActiveTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="s4y-chat-wrap">
            {activeTab === "chat" && (
              flashCardId
                ? <LiveChat bookingId={flashCardId} username={displayName} height="100%" />
                : <div className="s4y-tool-empty">
                    <div className="s4y-tool-empty-icon">💬</div>
                    <div className="s4y-tool-empty-title">Live Chat</div>
                    <div className="s4y-tool-empty-text">Start a live session to chat with your audience in real time.</div>
                  </div>
            )}
            {activeTab === "guests" && (
              <div className="s4y-tool-pane">
                <div className="s4y-tool-tab-header">
                  <span className="s4y-tool-tab-header-icon">👥</span>
                  <span className="s4y-tool-tab-header-title">Guest Management</span>
                  <span className="s4y-tool-tab-header-pill">LIVE</span>
                </div>
                <div className="s4y-tool-content">
                  {flashCardId
                    ? <GuestManagement bookingId={flashCardId} />
                    : <div className="s4y-tool-empty">
                        <div className="s4y-tool-empty-icon">👥</div>
                        <div className="s4y-tool-empty-title">Guest Management</div>
                        <div className="s4y-tool-empty-text">Invite and manage guests once your session is active.</div>
                      </div>
                  }
                </div>
              </div>
            )}
            {activeTab === "youtube" && (
              <div className="s4y-tool-pane">
                <div className="s4y-tool-tab-header">
                  <span className="s4y-tool-tab-header-icon">▶️</span>
                  <span className="s4y-tool-tab-header-title">YouTube Requests</span>
                  <span className="s4y-tool-tab-header-pill">QUEUE</span>
                </div>
                <div className="s4y-tool-content">
                  {flashCardId
                    ? <YoutubeRequest bookingId={flashCardId} />
                    : <div className="s4y-tool-empty">
                        <div className="s4y-tool-empty-icon">▶️</div>
                        <div className="s4y-tool-empty-title">YouTube Requests</div>
                        <div className="s4y-tool-empty-text">Audience video requests will appear here during your live session.</div>
                      </div>
                  }
                </div>
              </div>
            )}
            {activeTab === "polls" && (
              <div className="s4y-tool-pane">
                <div className="s4y-tool-tab-header">
                  <span className="s4y-tool-tab-header-icon">📊</span>
                  <span className="s4y-tool-tab-header-title">Live Polls</span>
                  <span className="s4y-tool-tab-header-pill">ENGAGE</span>
                </div>
                <div className="s4y-tool-content">
                  {flashCardId
                    ? <LivePolls bookingId={flashCardId} />
                    : <div className="s4y-tool-empty">
                        <div className="s4y-tool-empty-icon">📊</div>
                        <div className="s4y-tool-empty-title">Live Polls</div>
                        <div className="s4y-tool-empty-text">Create and run polls to engage your audience during a live session.</div>
                      </div>
                  }
                </div>
              </div>
            )}
          </div>

          {/* Reactions — always visible */}
          <div className="s4y-reactions">
            <div className="s4y-section-label">React</div>
            <div className="s4y-reactions-grid">
              {["🔥", "❤️", "🎤", "⭐", "🎵", "🎶", "🎼", "💕"].map((emoji) => (
                <button key={emoji} className="s4y-react-btn">{emoji}</button>
              ))}
            </div>
          </div>

          {/* Quick chat input — always visible */}
          <div className="s4y-chat-input-row">
            <input
              className="s4y-chat-input"
              placeholder="Say something..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setChatInput("")}
            />
            <button className="s4y-chat-send" onClick={() => setChatInput("")}>
              <Send sx={{ fontSize: "0.95rem" }} />
            </button>
          </div>
        </aside>
      </div>

      {/* ── Creator Tools drawer ─────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={toolsOpen}
        onClose={() => setToolsOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: "85vw", sm: 360 },
            background: "var(--s4y-bg-sidebar, #0d1021)",
            color: "#fff",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        {/* Drawer header */}
        <div className="s4y-drawer-header">
          <span className="s4y-drawer-title">
            <Build sx={{ fontSize: "1rem", color: "var(--s4y-orange, #f97316)" }} />
            Creator Tools
          </span>
          <button className="s4y-drawer-close" onClick={() => setToolsOpen(false)}>
            <Close sx={{ fontSize: "1rem" }} />
          </button>
        </div>

        {/* Tool list */}
        <div className="s4y-drawer-body">
          {(Object.keys(TOOL_META) as ToolId[]).map((id) => {
            const meta = TOOL_META[id];
            const on   = activeTools.includes(id);
            return (
              <div key={id} className={`s4y-tool-item ${on ? "active" : ""}`}>
                <div className="s4y-tool-item-info">
                  <span className="s4y-tool-item-title">
                    {meta.emoji}&nbsp; {meta.label}
                  </span>
                  <span className="s4y-tool-item-desc">{meta.desc}</span>
                </div>
                <button
                  className={`s4y-tool-toggle ${on ? "on" : "off"}`}
                  onClick={() => toggleTool(id)}
                >
                  {on ? "Remove" : "Add"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Karaoke Studio shortcut */}
        <div className="s4y-drawer-footer">
          <button
            className="s4y-drawer-studio-btn"
            onClick={() => { setAudioMixOpen(true); setToolsOpen(false); }}
          >
            🎤&nbsp; Open Karaoke Studio
          </button>
        </div>
      </Drawer>

      {/* ── AudioMix modal ───────────────────────────────────── */}
      {/* Always mounted so keepPlayingOnClose keeps audio running after the modal closes. */}
      <div
        className="s4y-audiomix-overlay"
        style={{ display: audioMixOpen ? undefined : "none" }}
        onClick={() => setAudioMixOpen(false)}
      >
        <div
          className="s4y-audiomix-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <AudioMixUpdated
            ref={audioMixRef}
            open={audioMixOpen}
            keepPlayingOnClose
            sessionId={flashCardId ?? undefined}
            onClose={() => setAudioMixOpen(false)}
            onLyricsChange={handleLyricsChange}
            onLyricsTimeChange={handleLyricsTimeChange}
            onPlayStateChange={handlePlayStateChange}
            onDurationChange={(d) => setKaraokeDuration(d)}
          />
        </div>
      </div>

      {/* ── End session dialog ───────────────────────────────── */}
      <Dialog
        open={showEndDialog}
        onClose={() => setShowEndDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { background: "#0f1225", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ color: "#fff", pb: 1 }}>End Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 1.5 }}>
            What would you like to do?
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
            <strong style={{ color: "rgba(255,255,255,0.8)" }}>Leave</strong> — you exit but the session continues.
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 0.75 }}>
            <strong style={{ color: "rgba(255,255,255,0.8)" }}>End Session</strong> — terminates for everyone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowEndDialog(false)}
            variant="outlined"
            size="small"
            sx={{ borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" }}
          >
            Cancel
          </Button>
          <Button onClick={handleLeave} variant="outlined" color="warning" size="small">
            Leave
          </Button>
          <Button onClick={handleEndStream} variant="contained" color="error" size="small">
            End Session
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

/* ── Page export — wraps inner component in AgoraRTCProvider ─── */
export function KaraokeCardLiveStreamTalent() {
  return (
    <AgoraWrapper mode="live" role="host">
      <KaraokeLiveInner />
    </AgoraWrapper>
  );
}

export default KaraokeCardLiveStreamTalent;
